import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { erc20Abi, formatUnits, type Address } from "viem";
import type { RedKeel } from "../config/networks";
import { usePools, type Pool } from "../hooks/usePools";
import { useCrear } from "../hooks/useCrear";
import { esEstable } from "../lib/precio";
import { planRescate } from "../lib/rescate";
import { num } from "../lib/formato";
import { useT } from "../lib/i18n";
import { Modal, Giro, Hueco } from "./Modal";
import { ElegirPool } from "./ElegirPool";
import { ModalOperacion, type EstadoOp } from "./ModalOperacion";

// slot0 truncado a (sqrtPriceX96, tick) + el tickSpacing real: igual que en CrearModal, sirve para
// Uniswap y para Slipstream (que devuelve un campo menos).
const POOL_ABI = [
  { type: "function", name: "slot0", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }] },
  { type: "function", name: "tickSpacing", stateMutability: "view", inputs: [], outputs: [{ type: "int24" }] },
] as const;

/**
 * TRAER UNA POSICIÓN QUE SE FUE DE RANGO POR ABAJO.
 *
 * Quien llega aquí no viene a invertir: viene con una pérdida. Entró con 1.000 $ en una pool de
 * Uniswap, el precio se salió del rango, se quedó con el token volátil y hoy son 860 $. Vender es
 * consagrar la pérdida; quedarse quieto es no cobrar nada mientras espera.
 *
 * Esta pantalla hace una sola pregunta —cuánto quieres recuperar— y responde a la única que importa
 * antes de firmar: A QUÉ PRECIO tiene que volver el mercado para que eso pase. Nada de porcentajes
 * abstractos: el número al que vuelves a tener tu dinero, y lo que cuesta llegar hasta ahí.
 *
 * SE DICE LA VERDAD INCÓMODA, y es deliberado: un LP va vendiendo por el camino, así que recuperar
 * 1.000 desde 860 NO es "que suba un 16%". Hay que llegar bastante más arriba que si simplemente
 * esperases con tus monedas en la wallet. A cambio, aquí se cobran comisiones durante la espera. Las
 * dos cifras se enseñan juntas para que la comparación la haga el dueño y no la pantalla.
 */
export function ModalRescate({
  abierto, cerrar, red, alAbrirVault,
}: {
  abierto: boolean;
  cerrar: () => void;
  red: RedKeel;
  alAbrirVault?: (v: Address) => void;
}) {
  const t = useT();
  const pools = usePools(red);
  const { address } = useAccount();
  const config = useConfig();

  const [elegida, setElegida] = useState<Pool | null>(null);
  const [cantidad, setCantidad] = useState("");
  const [objetivo, setObjetivo] = useState("");
  const [gas, setGas] = useState("");
  const [gasEnEstable, setGasEnEstable] = useState(true);
  const [cantEstable, setCantEstable] = useState("");
  // CON QUÉ LLEGA EL DUEÑO. No es un detalle de formulario: cambia lo que la piscina puede hacer.
  //   · "volatil": tiene el token barato -> el rango lo vende arriba y devuelve el objetivo.
  //   · "estable": ya vendió -> hay que VOLVER A COMPRAR el activo o no hay nada que se revalorice.
  //   · "ambas": el volátil arriba y el estable abajo, promediando mientras espera.
  const [modo, setModo] = useState<"volatil" | "estable" | "ambas">("volatil");

  const { crear, pasos, error, vaultCreado, ocupado, limpiar } = useCrear(red);

  const estableEsToken0 = elegida ? esEstable(elegida.token0.symbol) && !esEstable(elegida.token1.symbol) : true;
  const volatil = elegida ? (estableEsToken0 ? elegida.token1 : elegida.token0) : null;
  const estable = elegida ? (estableEsToken0 ? elegida.token0 : elegida.token1) : null;

  // LOS DOS SALDOS. El volátil es lo que trae de su posición anterior; el estable, lo que puede usar
  // para la bolsa de gas sin tocar ese volátil.
  const saldos = useQuery({
    queryKey: ["saldos-rescate", red.clave, elegida?.pool, address],
    enabled: !!volatil && !!estable && !!address,
    refetchInterval: 30_000,
    queryFn: async () => {
      const publico = getPublicClient(config, { chainId: red.idCadena });
      if (!publico) throw new Error("sin RPC");
      const [vol, est] = await publico.multicall({
        allowFailure: false,
        contracts: [
          { address: volatil!.address, abi: erc20Abi, functionName: "balanceOf", args: [address!] },
          { address: estable!.address, abi: erc20Abi, functionName: "balanceOf", args: [address!] },
        ],
      });
      return { volatil: vol as bigint, estable: est as bigint };
    },
  });
  const saldoVol = saldos.data ? Number(formatUnits(saldos.data.volatil, volatil!.decimals)) : 0;
  const saldoEst = saldos.data ? Number(formatUnits(saldos.data.estable, estable!.decimals)) : 0;

  // El precio y la rejilla REALES de la pool: la banda de rescate se calcula contra ellos.
  const precio = useQuery({
    queryKey: ["precio-rescate", red.clave, elegida?.pool],
    enabled: !!elegida && abierto,
    refetchInterval: 15_000,
    queryFn: async () => {
      const publico = getPublicClient(config, { chainId: red.idCadena })!;
      const [s0, ts] = await Promise.all([
        publico.readContract({ address: elegida!.pool, abi: POOL_ABI, functionName: "slot0" }),
        publico.readContract({ address: elegida!.pool, abi: POOL_ABI, functionName: "tickSpacing" }),
      ]);
      return { tick: s0[1] as number, spacing: Number(ts) };
    },
  });

  // El precio de hoy, suelto: hace falta para estimar la compra antes de que exista un plan.
  const precioHoy = useMemo(() => {
    if (!precio.data || !volatil || !estable) return 0;
    const s0 = Math.pow(1.0001, precio.data.tick / 2);
    return estableEsToken0
      ? (1 / (s0 * s0)) * 10 ** (volatil.decimals - estable.decimals)
      : s0 * s0 * 10 ** (volatil.decimals - estable.decimals);
  }, [precio.data, volatil, estable, estableEsToken0]);

  // Lo que se compraría del activo si llega en estable. El 0,3% de margen es el peaje típico de la
  // pool: se descuenta para no enseñar un rango más optimista que el que saldrá de verdad.
  const compraEstimada = useMemo(() => {
    if (modo !== "estable" || !precioHoy) return 0;
    const cap = Number(cantEstable) || 0;
    return cap > 0 ? (cap / precioHoy) * 0.997 : 0;
  }, [modo, cantEstable, precioHoy]);

  const plan = useMemo(() => {
    if (!elegida || !volatil || !estable || !precio.data) return null;
    return planRescate({
      tick: precio.data.tick,
      spacing: precio.data.spacing,
      estableEsToken0,
      decimalesEstable: estable.decimals,
      decimalesVolatil: volatil.decimals,
      // Llegando en estable, el volátil que entra en la banda es el que se COMPRA al abrir.
      cantidadVolatil: modo === "estable" ? compraEstimada : Number(cantidad),
      cantidadEstable: modo === "volatil" ? 0 : (modo === "estable" ? 0 : Number(cantEstable) || 0),
      objetivo: Number(objetivo),
      // El tope de fábrica del vault (`ilRecoverMaxTicks`): ~recuperar una caída del 50%.
      topeTicks: 6931,
    });
  }, [elegida, volatil, estable, estableEsToken0, cantidad, cantEstable, objetivo, precio.data, modo, compraEstimada]);

  // Sin LP de por medio: lo que subiría el precio si simplemente esperases con tus monedas.
  const subidaSiEsperas = plan && plan.valorHoy > 0 ? (Number(objetivo) / plan.valorHoy - 1) * 100 : 0;
  const objetivoValido = !!plan && Number(objetivo) >= plan.valorHoy && Number(objetivo) <= plan.valorHoy * 3;
  // Todo lo que sale del estable —el capital que se trae y, si se eligió, la bolsa de gas— tiene que
  // caber en el saldo de la wallet. Lo mismo para el volátil.
  const estableNecesario = (modo === "volatil" ? 0 : Number(cantEstable || 0)) + (gasEnEstable ? Number(gas || 0) : 0);
  const volatilNecesario = (modo === "estable" ? 0 : Number(cantidad || 0)) + (gasEnEstable ? 0 : Number(gas || 0));
  const gasCabe = estableNecesario <= saldoEst;
  const cantidadCabe = volatilNecesario <= saldoVol;
  const hayCapital = modo === "estable" ? Number(cantEstable) > 0 : Number(cantidad) > 0;
  const listo = !!elegida && hayCapital && cantidadCabe && objetivoValido && gasCabe && !!plan;

  // Mientras encadena las firmas —o si algo falla— el mismo modal de proceso que el resto de
  // operaciones, para que rescatar no se sienta distinto de cualquier otra cosa que hace la app.
  const op: EstadoOp | null = ocupado
    ? { fase: "cargando", titulo: t("Rescatando la posición"), sub: t("Confírmalo en tu wallet: son varias firmas encadenadas."), pasos }
    : error
      ? { fase: "error", titulo: "", mensaje: error, ...(pasos.length ? { pasos } : {}) }
      : vaultCreado
        ? { fase: "ok", titulo: t("Piscina de rescate abierta"), sub: t("El rango ya apunta a devolverte tu objetivo cuando el precio vuelva.") }
        : null;
  if (abierto && op) {
    // Al terminar bien, "Listo" CIERRA el rescate y lleva a la piscina nueva. Antes solo llamaba a
    // `limpiar`, que no tocaba `vaultCreado`: el modal de éxito se quedaba clavado (2026-09-29).
    const alCerrar = () => {
      if (ocupado) return;
      const nuevo = !error ? vaultCreado : null;
      limpiar();
      if (nuevo) {
        cerrar();
        alAbrirVault?.(nuevo);
      }
    };
    return <ModalOperacion estado={op} cerrar={alCerrar} red={red} />;
  }

  return (
    <Modal
      abierto={abierto}
      cerrar={cerrar}
      titulo={t("Rescatar una posición")}
      sub={t("Trae el capital de una pool que se te fue de rango y dinos cuánto quieres recuperar. Keel calcula el rango que te lo devuelve cuando el precio vuelva — sin vender nada ahora.")}
      ancho={720}
      pie={
        <button
          className="btn primario"
          disabled={!listo || ocupado}
          onClick={() => crear({
            pool: elegida!,
            // Llegando en estable, lo que se deposita ES el estable y la piscina compra el activo
            // dentro de la apertura; en los otros dos casos se deposita el volátil.
            cantidad: modo === "estable" ? cantEstable : cantidad,
            // La bolsa va en el token que haya elegido: si es el estable se deposita aparte
            // (`gasEnEstable`), y entonces del volátil no se retiene nada.
            gas: gasEnEstable ? "0" : (gas || "0"),
            gasEnEstable: gasEnEstable ? (gas || "0") : undefined,
            estableExtra: modo === "ambas" && Number(cantEstable) > 0 ? cantEstable : undefined,
            below: 0, above: 0, spacing: precio.data?.spacing ?? 60,
            depositaToken0: modo === "estable" ? estableEsToken0 : !estableEsToken0,
            comprarActivo: modo === "estable",
            reservaPct: 0,
            modoReinyeccion: 0, reinyeccionPct: 0, caidaPct: 0,
            reinvertir: true, // recuperando, la ganancia trabaja en vez de apartarse
            recuperar: objetivo,
          })}
        >
          {ocupado
            ? <Giro texto={t("Abriendo…")} />
            : listo
              ? t("Rescatar {n} {s} · recuperar {o} {e}", {
                  n: num(Number(modo === "estable" ? cantEstable : cantidad), modo === "estable" ? 2 : 4),
                  s: (modo === "estable" ? estable?.symbol : volatil?.symbol) ?? "",
                  o: num(Number(objetivo), 2), e: estable?.symbol ?? "",
                })
              : t("Rellena los datos para continuar")}
        </button>
      }
    >
      {pools.isPending && <Hueco alto={130} n={2} />}
      {pools.data && (
        <ElegirPool pools={pools.data} red={red} elegida={elegida} alElegir={setElegida} alAbrirVault={alAbrirVault} />
      )}

      {elegida && volatil && estable && (
        <>
          {/* DÓNDE ESTÁ EL PRECIO AHORA. Sin esto, el resto de la pantalla son números sin suelo:
              el objetivo a recuperar solo significa algo comparado con lo que vale hoy. */}
          <div className="panel resc-cabecera">
            <div className="aj-dinero-linea">
              <span>{t("Precio ahora")}</span>
              <strong>{plan ? `${num(plan.precioHoy, 4)} ${estable.symbol}` : "—"} <span className="tenue">{t("por {s}", { s: volatil.symbol })}</span></strong>
            </div>
            <div className="aj-dinero-linea">
              <span>{t("Tienes en la wallet")}</span>
              <strong>
                {num(saldoVol, 6)} {volatil.symbol} <span className="tenue">·</span> {num(saldoEst, 2)} {estable.symbol}
              </strong>
            </div>
          </div>

          {/* CON QUÉ LLEGAS. Es la primera pregunta porque cambia todo lo demás: quien conserva el
              token barato no tiene que comprar nada, y quien ya vendió sí. */}
          <div className="campo">
            <label><span><strong>1.</strong> {t("¿Con qué llegas?")}</span></label>
            <div className="ach-chips">
              <button className={"ach-chip" + (modo === "volatil" ? " on" : "")} onClick={() => setModo("volatil")}>
                {t("Con {s}", { s: volatil.symbol })}
              </button>
              <button className={"ach-chip" + (modo === "estable" ? " on" : "")} onClick={() => setModo("estable")}>
                {t("Ya vendí: {s}", { s: estable.symbol })}
              </button>
              <button className={"ach-chip" + (modo === "ambas" ? " on" : "")} onClick={() => setModo("ambas")}>
                {t("Las dos")}
              </button>
            </div>
            <p className="detalle tenue">
              {modo === "volatil"
                ? t("Conservas el token que se abarató. No se vende nada: el rango lo irá vendiendo según el precio se recupere.")
                : modo === "estable"
                  ? t("Vendiste al bajar, así que ahora mismo no tienes nada que se revalorice. Para recuperar por arriba hay que VOLVER A COMPRAR {v} — la piscina lo hace al abrir, y eso es tomar exposición otra vez.", { v: volatil.symbol })
                  : t("El {v} se vende arriba y el {e} compra abajo mientras esperas.", { v: volatil.symbol, e: estable.symbol })}
            </p>
          </div>

          {/* 2. lo que trae */}
          {modo !== "estable" && (
          <div className="campo">
            <label>
              <span><strong>2.</strong> {t("Cuánto {s} traes", { s: volatil.symbol })}</span>
              {saldos.data && (
                <button className="btn fantasma mini" onClick={() => setCantidad(formatUnits(saldos.data!.volatil, volatil.decimals))}>
                  {t("MÁX {n}", { n: num(saldoVol, 6) })}
                </button>
              )}
            </label>
            <input value={cantidad} onChange={(e) => setCantidad(e.target.value)} placeholder={`0,0 ${volatil.symbol}`} inputMode="decimal" />
            <p className="detalle tenue">
              {plan && Number(cantidad) > 0
                ? t("Hoy valen {n} {s}. Eso es lo que recuperarías vendiendo ahora mismo.", { n: num(plan.valorHoy, 2), s: estable.symbol })
                : t("El token que te quedó cuando la pool se salió de rango. No se vende nada de esto.")}
            </p>
          </div>
          )}

          {/* el estable: capital cuando se llega vendido, o el lado de abajo cuando se traen los dos */}
          {modo !== "volatil" && (
          <div className="campo">
            <label>
              <span>{modo === "estable" ? <><strong>2.</strong> {t("Cuánto {s} traes", { s: estable.symbol })}</> : t("¿Traes también {s}? (opcional)", { s: estable.symbol })}</span>
              {saldos.data && saldoEst > 0 && (
                <button className="btn fantasma mini" onClick={() => setCantEstable(String(Math.max(0, saldoEst - Number(gasEnEstable ? gas || 0 : 0))))}>
                  {t("MÁX {n}", { n: num(saldoEst, 2) })}
                </button>
              )}
            </label>
            <input value={cantEstable} onChange={(e) => setCantEstable(e.target.value)} placeholder={`0,00 ${estable.symbol}`} inputMode="decimal" />
            <p className="detalle tenue">
              {Number(cantEstable) > 0
                ? t("Va al lado de ABAJO del rango: si {v} sigue cayendo, ese {e} lo compra más barato solo, sin swap y sin deslizamiento. Y si sube, se queda quieto.", { v: volatil.symbol, e: estable.symbol })
                : t("Déjalo en blanco si solo traes {v}. Si añades {e}, el rango se abre por abajo para promediar mientras esperas.", { v: volatil.symbol, e: estable.symbol })}
            </p>
          </div>
          )}

          {/* 2. el objetivo, con atajos para no escribir a ciegas */}
          <div className="campo">
            <label><span><strong>3.</strong> {t("Cuánto quieres recuperar ({s})", { s: estable.symbol })}</span></label>
            <input value={objetivo} onChange={(e) => setObjetivo(e.target.value)} placeholder={plan ? num(plan.valorHoy, 2) : "0,00"} inputMode="decimal" />
            {plan && plan.valorHoy > 0 && (
              <div className="ach-chips" style={{ marginTop: 8 }}>
                {[1.1, 1.16, 1.25, 1.5].map((f) => (
                  <button key={f} className="ach-chip" onClick={() => setObjetivo((plan.valorHoy * f).toFixed(2))}>
                    +{Math.round((f - 1) * 100)}%
                  </button>
                ))}
              </div>
            )}
            <p className="detalle tenue">
              {t("Lo que pusiste en la pool original, normalmente. El rango se dimensiona para devolverte exactamente esto.")}
            </p>
          </div>

          {/* 3. la bolsa de gas, y de dónde sale */}
          <div className="campo">
            <label><span><strong>4.</strong> {t("Bolsa de gas")}</span></label>
            <div className="ach-chips" style={{ marginBottom: 8 }}>
              <button className={"ach-chip" + (gasEnEstable ? " on" : "")} onClick={() => setGasEnEstable(true)}>
                {t("En {s}", { s: estable.symbol })}
              </button>
              <button className={"ach-chip" + (!gasEnEstable ? " on" : "")} onClick={() => setGasEnEstable(false)}>
                {t("En {s}", { s: volatil.symbol })}
              </button>
            </div>
            <input value={gas} onChange={(e) => setGas(e.target.value)}
              placeholder={gasEnEstable ? `0,0 ${estable.symbol}` : `0,0 ${volatil.symbol}`} inputMode="decimal" />
            <p className="detalle tenue">
              {gasEnEstable
                ? t("Sale aparte, de tus {e} — así no tocas el {v} que vienes a recuperar. Paga los reajustes del robot y lo que no se gaste vuelve entero al cerrar. En esta red conviene {g} {e}.", { e: estable.symbol, v: volatil.symbol, g: String(red.gasMin ?? 10) })
                : t("Sale de lo que traes: una parte de tu {v} se retiene para pagar los reajustes. Lo que no se gaste vuelve entero al cerrar.", { v: volatil.symbol })}
            </p>
            {!gasCabe && (
              <p className="detalle" style={{ color: "#ffb37a" }}>
                {t("Entre el capital y la bolsa hacen falta {n} {s}, y solo tienes {t}.", {
                  n: num(estableNecesario, 2), s: estable.symbol, t: num(saldoEst, 2),
                })}
              </p>
            )}
            {!cantidadCabe && (
              <p className="detalle" style={{ color: "#ffb37a" }}>
                {t("Entre el capital y la bolsa hacen falta {n} {s}, y solo tienes {t}.", {
                  n: num(volatilNecesario, 6), s: volatil.symbol, t: num(saldoVol, 6),
                })}
              </p>
            )}
          </div>

          {/* 4. lo que va a pasar */}
          {plan && Number(cantidad) > 0 && objetivoValido && (
            <div className="panel">
              {modo === "estable" && compraEstimada > 0 && (
                <div className="aj-dinero-linea">
                  <span>{t("Se comprará ahora")}</span>
                  <strong style={{ color: "#ffb37a" }}>
                    ≈ {num(compraEstimada, 6)} {volatil.symbol} <span className="tenue">{t("a {p} {e}", { p: num(precioHoy, 4), e: estable.symbol })}</span>
                  </strong>
                </div>
              )}
              <div className="aj-dinero-linea">
                <span>{t("El rango quedará en")}</span>
                <strong>{num(plan.precioMin, 4)} — {num(plan.precioMax, 4)} {estable.symbol}</strong>
              </div>
              <div className="aj-dinero-linea">
                <span>{t("Amplitud")}</span>
                <strong>{num(plan.anchoPct, 1)}%</strong>
              </div>
              {Number(cantEstable) > 0 && (
                <div className="aj-dinero-linea">
                  <span>{t("Tu {e} compra {v} hasta", { e: estable.symbol, v: volatil.symbol })}</span>
                  <strong>{num(plan.precioMin, 4)} {estable.symbol}</strong>
                </div>
              )}
              <div className="aj-dinero-linea">
                <span>{t("Recuperas tus {n} {s} cuando {v} llegue a", { n: num(Number(objetivo), 2), s: estable.symbol, v: volatil.symbol })}</span>
                <strong style={{ color: "#4bd6a0" }}>{num(plan.precioSalida, 4)} {estable.symbol}</strong>
              </div>
              <div className="aj-dinero-linea">
                <span>{t("Tiene que subir")}</span>
                <strong>+{num(plan.subidaPct, 1)}%</strong>
              </div>
              <p className="detalle">
                {t("Esperando con tus {s} en la wallet te bastaría +{p}%. Aquí hace falta más porque la piscina va VENDIENDO por el camino, a precios intermedios — a cambio cobras comisiones durante toda la espera y hoy no vendes nada.", {
                  s: volatil.symbol, p: num(subidaSiEsperas, 1),
                })}
              </p>
              {modo === "estable" && (
                <p className="detalle" style={{ color: "#ffb37a" }}>
                  {t("Vuelves a tener {v}: si sigue bajando, pierdes más que quedándote en {e}. Lo que ganas a cambio es que el rango cobra comisiones y te devuelve el objetivo cuando suba.", { v: volatil.symbol, e: estable.symbol })}
                </p>
              )}
              {plan.recortado && (
                <p className="detalle" style={{ color: "#ffb37a" }}>
                  {t("Ese objetivo queda más lejos de lo que el vault estira por defecto: el rango se abrirá recortado y recuperarás menos de lo que pides. Baja el objetivo o súbelo luego en Ajustes → Recuperación.")}
                </p>
              )}
            </div>
          )}

          {plan && Number(cantidad) > 0 && !objetivoValido && (
            <p className="detalle" style={{ color: "#ffb37a" }}>
              {Number(objetivo) > 0 && Number(objetivo) < plan.valorHoy
                ? t("Pedir menos de lo que traes no tiene sentido: el mínimo es {n} {s}.", { n: num(plan.valorHoy, 2), s: estable.symbol })
                : Number(objetivo) > plan.valorHoy * 3
                  ? t("Como mucho 3× lo que traes ({n} {s}): más allá el precio tendría que multiplicarse y el rango se recortaría igual.", { n: num(plan.valorHoy * 3, 2), s: estable.symbol })
                  : t("Escribe cuánto quieres recuperar para ver el rango que saldría.")}
            </p>
          )}
        </>
      )}

    </Modal>
  );
}
